// Typed fetch wrapper for the Clearpol Compliance API. Cookie session auth (same origin), CSRF header on every
// mutation, optimistic concurrency (If-Match), change reasons, and one error type that matches the server's
// { error: { code, message, details } } envelope. No React imports here, so it is unit-testable in node.

export interface ApiErrorEnvelope {
  error: { code: string; message: string; details?: unknown }
}

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: unknown

  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details ?? null
  }

  get isUnauthorized(): boolean {
    return this.status === 401
  }
  get isForbidden(): boolean {
    return this.status === 403
  }
  get isNotFound(): boolean {
    return this.status === 404
  }
  get isConflict(): boolean {
    return this.status === 409
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError
}

export type QueryValue = string | number | boolean | null | undefined | readonly string[]
export type Query = Record<string, QueryValue>

/** Builds a same-origin URL. Arrays become comma-joined values (the API's __in / __contains syntax). Empty values are dropped. */
export function buildUrl(path: string, query?: Query): string {
  if (!query) return path
  const params = new URLSearchParams()
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue
    if (Array.isArray(v)) {
      if (v.length) params.set(k, v.join(','))
      continue
    }
    params.set(k, String(v))
  }
  const qs = params.toString()
  return qs ? `${path}${path.includes('?') ? '&' : '?'}${qs}` : path
}

const STATUS_MESSAGES: Record<number, string> = {
  400: 'The request was not accepted. Check the highlighted fields and try again.',
  401: 'Your session has ended. Sign in with Microsoft to continue.',
  403: 'You do not have permission to do that.',
  404: 'That record could not be found. It may have been archived or removed.',
  409: 'This record changed since you opened it.',
  413: 'The upload is too large.',
  429: 'Too many requests. Wait a moment and try again.',
  500: 'Something went wrong on the server. Try again, and contact an admin if it keeps happening.',
  502: 'The Clearpol Compliance server is not responding. Try again in a moment.',
  503: 'The Clearpol Compliance server is temporarily unavailable. Try again in a moment.',
}

function statusCode(status: number): string {
  if (status === 400) return 'bad_request'
  if (status === 401) return 'unauthenticated'
  if (status === 403) return 'forbidden'
  if (status === 404) return 'not_found'
  if (status === 409) return 'conflict'
  return status >= 500 ? 'internal' : 'error'
}

/** Converts any response body into an ApiError, preferring the server's envelope. */
export function toApiError(status: number, body: unknown): ApiError {
  const env = body as Partial<ApiErrorEnvelope> | null
  if (env && typeof env === 'object' && env.error && typeof env.error === 'object') {
    const { code, message, details } = env.error as { code?: unknown; message?: unknown; details?: unknown }
    return new ApiError(
      status,
      typeof code === 'string' && code ? code : statusCode(status),
      typeof message === 'string' && message
        ? message
        : (STATUS_MESSAGES[status] ?? `Request failed (${status})`),
      details,
    )
  }
  return new ApiError(status, statusCode(status), STATUS_MESSAGES[status] ?? `Request failed (${status})`)
}

// ------------------------------------------------------------------ client state

let csrfToken: string | null = null
let unauthorizedHandler: ((err: ApiError) => void) | null = null
let csrfRefresher: (() => Promise<string | null>) | null = null

/** Set from GET /auth/me. Sent as X-CSRF-Token on every non-GET request. */
export function setCsrfToken(token: string | null): void {
  csrfToken = token
}
export function getCsrfToken(): string | null {
  return csrfToken
}

/** Called when a request (other than the session probe) returns 401, e.g. the session expired mid-use. */
export function setUnauthorizedHandler(fn: ((err: ApiError) => void) | null): void {
  unauthorizedHandler = fn
}

/** Re-reads the CSRF token (GET /auth/me) when the server rejects one, e.g. after a session refresh in another tab. */
export function setCsrfRefresher(fn: (() => Promise<string | null>) | null): void {
  csrfRefresher = fn
}

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'

export interface RequestOptions {
  query?: Query
  body?: unknown
  headers?: Record<string, string>
  /** Optimistic concurrency: the record version you last read. The server answers 409 if it changed. */
  ifMatch?: number
  /** Why the change is being made. Stored in history and the audit log. */
  reason?: string
  idempotencyKey?: string
  signal?: AbortSignal
  /** Do not trigger the global sign-in redirect on 401 (used by the session probe). */
  skipAuthRedirect?: boolean
  /** Internal: set on the single retry after a CSRF refresh. */
  csrfRetried?: boolean
}

const MUTATING = new Set<HttpMethod>(['POST', 'PATCH', 'PUT', 'DELETE'])

export async function apiRequest<T = unknown>(
  method: HttpMethod,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json', ...opts.headers }
  const init: RequestInit = { method, headers, credentials: 'same-origin', signal: opts.signal }
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json'
    init.body = JSON.stringify(opts.body)
  }
  if (MUTATING.has(method)) {
    if (csrfToken) headers['X-CSRF-Token'] = csrfToken
    if (opts.reason?.trim()) headers['X-Change-Reason'] = encodeURIComponent(opts.reason.trim())
    if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey
  }
  if (opts.ifMatch !== undefined) headers['If-Match'] = `"${opts.ifMatch}"`

  let res: Response
  try {
    res = await globalThis.fetch(buildUrl(path, opts.query), init)
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err
    throw new ApiError(
      0,
      'network_error',
      'Cannot reach the Clearpol Compliance server. Check your connection and try again.',
    )
  }

  if (res.status === 204 || res.status === 205) return undefined as T
  const text = await res.text()
  let body: unknown = null
  if (text) {
    try {
      body = JSON.parse(text)
    } catch {
      body = text
    }
  }
  if (!res.ok) {
    const err = toApiError(res.status, body)
    if (
      err.status === 403 &&
      err.code === 'csrf_failed' &&
      MUTATING.has(method) &&
      !opts.csrfRetried &&
      csrfRefresher
    ) {
      const fresh = await csrfRefresher().catch(() => null)
      if (fresh) {
        csrfToken = fresh
        return apiRequest<T>(method, path, { ...opts, csrfRetried: true })
      }
    }
    if (err.status === 401 && !opts.skipAuthRedirect) unauthorizedHandler?.(err)
    throw err
  }
  return body as T
}

/** multipart/form-data upload (POST /api/v1/files). Same CSRF, error and 401 handling as JSON requests. */
export async function apiUpload<T = unknown>(
  path: string,
  form: FormData,
  opts: { signal?: AbortSignal } = {},
): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (csrfToken) headers['X-CSRF-Token'] = csrfToken
  let res: Response
  try {
    res = await globalThis.fetch(path, {
      method: 'POST',
      body: form,
      headers,
      credentials: 'same-origin',
      signal: opts.signal,
    })
  } catch (err) {
    if ((err as { name?: string })?.name === 'AbortError') throw err
    throw new ApiError(
      0,
      'network_error',
      'Cannot reach the Clearpol Compliance server. Check your connection and try again.',
    )
  }
  const text = await res.text()
  let body: unknown = null
  try {
    body = text ? JSON.parse(text) : null
  } catch {
    body = text
  }
  if (!res.ok) {
    const err = toApiError(res.status, body)
    if (err.status === 401) unauthorizedHandler?.(err)
    throw err
  }
  return body as T
}

export const api = {
  get: <T = unknown>(path: string, opts?: RequestOptions) => apiRequest<T>('GET', path, opts),
  post: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiRequest<T>('POST', path, { ...opts, body: body ?? {} }),
  patch: <T = unknown>(path: string, body: unknown, opts?: RequestOptions) =>
    apiRequest<T>('PATCH', path, { ...opts, body }),
  put: <T = unknown>(path: string, body: unknown, opts?: RequestOptions) =>
    apiRequest<T>('PUT', path, { ...opts, body }),
  del: <T = unknown>(path: string, body?: unknown, opts?: RequestOptions) =>
    apiRequest<T>('DELETE', path, { ...opts, body }),
}

/** Field-level validation issues from a 400 response: details is [{ path, message }] (Zod issues). */
export function fieldIssues(err: unknown): Record<string, string> {
  if (!isApiError(err) || !Array.isArray(err.details)) return {}
  const out: Record<string, string> = {}
  for (const d of err.details as Array<{ path?: unknown; message?: unknown }>) {
    const path = Array.isArray(d?.path) ? d.path.join('.') : typeof d?.path === 'string' ? d.path : ''
    const field = path.split('.')[0]
    if (field && typeof d.message === 'string' && !out[field]) out[field] = d.message
  }
  return out
}

/** One readable line for a toast. */
export function errorMessage(err: unknown): string {
  if (isApiError(err)) {
    const issues = fieldIssues(err)
    const first = Object.entries(issues)[0]
    if (err.status === 400 && first) return `${err.message}: ${first[0]} ${first[1]}`
    return err.message
  }
  if (err instanceof Error && err.message) return err.message
  return 'Something went wrong.'
}

/**
 * One toast line for a batch of writes that failed (mapping several records at once): how many failed, then the
 * server's message for each distinct reason. A 400 link_rejected carries its reason ("miscellaneous evidence belongs
 * to one control only; ..."), so the line explains itself, once, however many records it refused.
 * `what` is the label for the records (a fixed string, or singular and plural forms).
 */
export function batchFailureMessage(
  errors: readonly unknown[],
  what: string | { one: string; many: string },
  verb = 'map',
): string | null {
  if (!errors.length) return null
  const n = errors.length
  const label = typeof what === 'string' ? what : n === 1 ? what.one : what.many
  const reasons = [...new Set(errors.map(errorMessage))]
  return `Could not ${verb} ${n} ${label}: ${reasons.join('; ')}`
}
